import {
  COMPLETED_STATUS,
  FAILED_STATUS,
  RUNNING_STATUS,
  normalizeStatus,
} from './thinkingStatus.js';
import { PROVIDER_TRACE_TOKENS, formatExecutionDetail } from './thinkingProcessDetail.js';
import { formatSourceLines } from './thinkingProcessActivityProjection.js';

const BLUEPRINT_SECTION_LABELS = [
  ['任务步骤:', '任务步骤'],
];
const SOURCING_STAGE_LABELS = {
  prepare_context: '上下文',
  route_sources: '数据源',
  search_instance_db: '实例库',
  search_mcp: 'MCP',
  search_local_knowledge: '知识库',
  search_external_fallback: '外部源',
  rerank_candidates: '重排',
  build_shortlist: '名单',
  persist_context_patch: '沉淀',
  finalize: '完成',
};

const STATUS_FORMATTERS = [
  [COMPLETED_STATUS, 'completed'],
  [FAILED_STATUS, 'failed'],
  [RUNNING_STATUS, 'running'],
];
function normalizeText(value = '') {
  return String(value || '').trim();
}
function normalizeLower(value = '') {
  return normalizeText(value).toLowerCase();
}
function toLines(value = '') {
  return String(value || '')
    .split('\n')
    .map(normalizeText)
    .filter(Boolean);
}
function uniqueStrings(items = []) {
  return [...new Set(items.map(normalizeText).filter(Boolean))];
}
function compactLineParts(items = []) {
  return items.map(normalizeText).filter(Boolean).join(' · ');
}

function resolveObject(value = null) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function formatStepStatus(status = '') {
  const normalized = normalizeStatus(status);
  return STATUS_FORMATTERS.find(([knownStatuses]) => knownStatuses.has(normalized))?.[1] || normalized || 'running';
}

function countNumberedSectionItems(lines = [], sectionTitle = '') {
  const title = normalizeText(sectionTitle);
  if (!title) {
    return 0;
  }
  const startIndex = lines.findIndex((line) => normalizeText(line) === title);
  if (startIndex < 0) {
    return 0;
  }
  const sectionLines = lines.slice(startIndex + 1);
  const endIndex = sectionLines.findIndex((line) => {
    const text = normalizeText(line);
    return text.endsWith(':') || text.endsWith('：');
  });
  return sectionLines
    .slice(0, endIndex < 0 ? sectionLines.length : endIndex)
    .filter((line) => /^\d+\./.test(normalizeText(line)))
    .length;
}

function formatProcessBlueprintDetail(detail = '') {
  const lines = toLines(detail);
  const stepSummary = BLUEPRINT_SECTION_LABELS
    .map(([sectionTitle, displayLabel]) => [
      displayLabel,
      countNumberedSectionItems(lines, sectionTitle),
    ])
    .filter(([, count]) => count > 0)
    .map(([, count]) => `已准备 ${count} 步`);
  return stepSummary[0] || '';
}

function shouldHideExecutionTrace(trace = {}) {
  const stepId = normalizeLower(trace.step_id);
  const label = normalizeLower(trace.label);
  const detail = normalizeLower([trace.detail, trace.reason].find(Boolean));
  const isProviderTrace = PROVIDER_TRACE_TOKENS.some((token) => detail.includes(token));
  const status = normalizeStatus(trace.status);
  const isTerminal = [COMPLETED_STATUS, FAILED_STATUS].some((statuses) => statuses.has(status));
  return [isProviderTrace, !isTerminal,
    [stepId.includes('llm'), label.includes('生成')].some(Boolean)].every(Boolean);
}

function formatSourcingObservationDetail(trace = {}) {
  const observation = resolveObject(trace.observation);
  const stageId = normalizeText(observation.stage_id);
  if (!stageId) {
    return formatExecutionDetail([trace.detail, trace.reason].find(Boolean) ?? '');
  }
  const hitCount = Number(observation.hit_count ?? 0);
  const source = normalizeText(observation.source);
  return compactLineParts([
    [SOURCING_STAGE_LABELS[stageId], stageId].find(Boolean),
    hitCount > 0 ? `命中 ${hitCount}` : '',
    source ? `来源 ${source}` : '',
    normalizeText(observation.reason),
  ]);
}

function formatTraceDetail(trace = {}) {
  const rawDetail = trace.detail || trace.reason || '';
  const stepId = normalizeLower(trace?.step_id);
  if (stepId.includes('sourcing_observation')) {
    return formatSourcingObservationDetail(trace);
  }
  return stepId.includes('process_blueprint')
    ? formatProcessBlueprintDetail(rawDetail)
    : formatExecutionDetail(rawDetail);
}

function isAnswerGenerationTrace(trace = {}) {
  const stepId = normalizeLower(trace.step_id);
  const label = normalizeLower(trace.label);
  return stepId.includes('llm') || label.includes('生成');
}

function publicTraceLabel(trace = {}) {
  if (isAnswerGenerationTrace(trace)) return '回答生成';
  const label = normalizeText(trace.label);
  const internalTokens = uniqueStrings([normalizeText(resolveObject(trace.observation).stage_id),
    ...normalizeText(trace.step_id).split(':').slice(1)]);
  const publicLabel = internalTokens.reduce((text, token) => text.replaceAll(token, ''), label).replace(/\s+/g, ' ').trim();
  return !publicLabel || /^[a-z0-9_:-]+$/i.test(publicLabel) ? '执行步骤' : publicLabel;
}

function formatTerminalFallbackDetail(trace = {}) {
  if (!isAnswerGenerationTrace(trace)) {
    return '';
  }
  const status = normalizeStatus(trace?.status);
  if (COMPLETED_STATUS.has(status)) {
    return '回复已生成。';
  }
  if (FAILED_STATUS.has(status)) {
    return '回复暂时没有生成成功。';
  }
  return '';
}

function formatQueryDetail(detail = '', hasSources = false) {
  return hasSources && detail && !detail.startsWith('查询 · ')
    ? `查询 · ${detail}`
    : detail;
}

function formatExecutionCardLine(trace = {}) {
  const label = publicTraceLabel(trace);
  const detail = [formatTraceDetail(trace), formatTerminalFallbackDetail(trace)].find(Boolean);
  const sourceLines = formatSourceLines(trace.sources);
  if ([!detail, sourceLines.length === 0, isAnswerGenerationTrace(trace), isRunningTrace(trace)].every(Boolean)) {
    return '';
  }
  if ([!detail, sourceLines.length === 0, label === '执行步骤'].every(Boolean)) {
    return '';
  }
  if (isAnswerGenerationTrace(trace)) {
    return compactLineParts([`[${formatStepStatus(trace.status)}] ${label}`, detail]);
  }
  const queryDetail = formatQueryDetail(detail, sourceLines.length > 0);
  return [
    `[${formatStepStatus(trace.status)}] ${label}`,
    queryDetail,
    sourceLines.length ? `读取 ${sourceLines.length} 份资料` : '',
    ...sourceLines,
  ].filter(Boolean).join('\n');
}

function traceIdentity(trace = {}) {
  return normalizeText(trace.step_id || trace.node_id || trace.label || trace.agent);
}

function isRunningTrace(trace = {}) {
  return RUNNING_STATUS.has(normalizeStatus(trace?.status));
}

function orderDisplayExecutionQueue(queue = []) {
  const { settled, running } = queue.reduce((groups, trace) => {
    groups[isRunningTrace(trace) ? 'running' : 'settled'].push(trace);
    return groups;
  }, { settled: [], running: [] });
  return [...settled, ...running];
}

function buildDisplayExecutionQueue(executionCards = []) {
  const queue = [];
  const indexByIdentity = new Map();
  (Array.isArray(executionCards) ? executionCards : [])
    .filter((trace) => Object.keys(resolveObject(trace)).length > 0)
    .filter((trace) => !shouldHideExecutionTrace(trace))
    .forEach((trace) => {
      const identity = traceIdentity(trace) || `trace:${queue.length}`;
      const existingIndex = indexByIdentity.get(identity);
      if (Number.isInteger(existingIndex)) {
        queue[existingIndex] = trace;
        return;
      }
      indexByIdentity.set(identity, queue.length);
      queue.push(trace);
    });
  return orderDisplayExecutionQueue(queue);
}


export { buildDisplayExecutionQueue, formatExecutionCardLine, formatStepStatus };
